import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { AuthProvider } from './hooks/useAuth';
import { NotificationProvider } from './hooks/useNotification';
import { theme } from './theme';
import { ScrollToTop } from './components/common/ScrollToTop';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import ResponsiveAppBar from './components/ResponsiveAppBar';
import { LandingPage } from './components/home/LandingPage';
import { useSecurityMeasures } from './security/utils';
import { securityConfig } from './security/config';
import { ErrorBoundary } from './components/ErrorBoundary';
import { captureReferralParam } from './utils/referral';


const Login = React.lazy(() => import('./components/auth/Login'));
const Register = React.lazy(() =>
  import('./components/auth/Register').then((mod) => ({ default: mod.Register }))
);
const ForgotPassword = React.lazy(() =>
  import('./components/auth/ForgotPassword').then((mod) => ({ default: mod.ForgotPassword }))
);
const ResetPassword = React.lazy(() =>
  import('./components/auth/ResetPassword').then((mod) => ({ default: mod.ResetPassword }))
);
const VerifyEmail = React.lazy(() =>
  import('./components/auth/VerifyEmail').then((mod) => ({ default: mod.VerifyEmail }))
);
const TwoFactorSetup = React.lazy(() =>
  import('./components/auth/TwoFactorSetup').then((mod) => ({ default: mod.TwoFactorSetup }))
);
const TwoFactorVerification = React.lazy(() =>
  import('./components/auth/TwoFactorVerification').then((mod) => ({
    default: mod.TwoFactorVerification,
  }))
);
const SessionManagement = React.lazy(() =>
  import('./components/auth/SessionManagement').then((mod) => ({ default: mod.SessionManagement }))
);
const SocialAccountManager = React.lazy(() =>
  import('./components/auth/SocialAccountManager').then((mod) => ({
    default: mod.SocialAccountManager,
  }))
);
const OAuthCallback = React.lazy(() => import('./pages/OAuthCallback'));
const PreLoginProductPicker = React.lazy(() => import('./pages/PreLoginProductPicker'));
const SelectRole = React.lazy(() => import('./pages/onboarding/SelectRole'));
const Dashboard = React.lazy(() =>
  import('./components/dashboard/Dashboard').then((mod) => ({ default: mod.Dashboard }))
);
const Whitepaper = React.lazy(() => import('./components/home/Whitepaper'));
const FamilyCareLandingPage = React.lazy(() => import('./pages/family-care/FamilyCareLandingPage'));
const FamilyCareGuidePage = React.lazy(() => import('./pages/family-care/FamilyCareGuidePage'));
const FamilyCareBookingPage = React.lazy(() => import('./pages/family-care/FamilyCareBookingPage'));
const FamilyCareBookingCompletePage = React.lazy(() => import('./pages/family-care/FamilyCareBookingCompletePage'));
const FamilyCareCaregiverApplicationPage = React.lazy(() => import('./pages/family-care/FamilyCareCaregiverApplicationPage'));
const FamilyCareCaregiverInvitePage = React.lazy(() => import('./pages/family-care/FamilyCareCaregiverInvitePage'));
const FamilyCareParentDashboard = React.lazy(() => import('./pages/family-care/FamilyCareParentDashboard'));
const FamilyCareWorkerDashboard = React.lazy(() => import('./pages/family-care/FamilyCareWorkerDashboard'));
const FamilyCareAdminDashboard = React.lazy(() => import('./pages/family-care/FamilyCareAdminDashboard'));
const FamilyCareAdminGuidePage = React.lazy(() => import('./pages/family-care/FamilyCareAdminGuidePage'));
const FamilyCareSettingsPage = React.lazy(() => import('./pages/family-care/FamilyCareSettingsPage'));
const GovernableAiLandingPage = React.lazy(() => import('./pages/governable-ai/GovernableAiLandingPage'));
const GovernableAiToolkitPage = React.lazy(() => import('./pages/governable-ai/GovernableAiToolkitPage'));
const GovernableAiWorkspacePage = React.lazy(() => import('./pages/governable-ai/GovernableAiWorkspacePage'));

// Legal pages
const TermsOfService = React.lazy(() => import('./pages/legal/TermsOfService'));
const PrivacyPolicy = React.lazy(() => import('./pages/legal/PrivacyPolicy'));
const ContactUs = React.lazy(() => import('./pages/legal/ContactUs'));

// Lazy load heavy pages for better performance
const SubmitFraudReportPage = React.lazy(() => import('./pages/fraud/SubmitFraudReportPage'));
const MyReportsPage = React.lazy(() => import('./pages/fraud/MyReportsPage'));
const ReportDetailsPage = React.lazy(() => import('./pages/fraud/ReportDetailsPage'));
const TrustScoreScanPage = React.lazy(() => import('./pages/TrustScoreScanPage'));
const TrustScoreDashboardPage = React.lazy(() => import('./pages/TrustScoreDashboardPage'));
const FraudReporting = React.lazy(() => import('./components/dashboard/FraudReporting'));
const Support = React.lazy(() => import('./components/dashboard/Support'));
const VCNPage = React.lazy(() => import('./pages/enterprise/VCNPage'));
const GatewaySnifferPage = React.lazy(() => import('./pages/enterprise/GatewaySnifferPage'));
const VCNLandingPage = React.lazy(() => import('./pages/enterprise/VCNLandingPage'));
const GatewaySnifferLandingPage = React.lazy(() => import('./pages/enterprise/GatewaySnifferLandingPage'));
const GateKeeperXLandingPage = React.lazy(() => import('./pages/enterprise/GateKeeperXLandingPage'));
const SandboxLandingPage = React.lazy(() => import('./pages/sandbox/SandboxLandingPage'));
const GateKeeperXPage = React.lazy(() => import('./pages/enterprise/GateKeeperXPage'));
const MediaProofLandingPage = React.lazy(() => import('./pages/MediaProofLandingPage'));
const RagIntegritySentinelLandingPage = React.lazy(() => import('./pages/RagIntegritySentinelLandingPage'));
const ResearchPapersPage = React.lazy(() => import('./pages/ResearchPapersPage'));
const NativeTongueLandingPage = React.lazy(() => import('./pages/NativeTongueLandingPage'));
const Profile = React.lazy(() => import('./pages/Profile'));
const InvestigatorAssignedReportsPage = React.lazy(() => import('./pages/fraud/InvestigatorAssignedReportsPage'));
const AdminDashboard = React.lazy(() => import('./pages/AdminDashboard'));
const TrustScoreDetailsPage = React.lazy(() => import('./pages/TrustScoreDetailsPage'));
const TrustScoreAnalyticsPage = React.lazy(() => import('./pages/TrustScoreAnalyticsPage'));

// Lazy load Investigator Marketplace Pages
const InvestigatorMarketplacePage = React.lazy(() => import('./pages/investigator/InvestigatorMarketplacePage'));

// Lazy load Dispute Pages
const SubmitDisputePage = React.lazy(() => import('./pages/dispute/SubmitDisputePage'));
const MyDisputesPage = React.lazy(() => import('./pages/dispute/MyDisputesPage'));
const DisputeDetailsPage = React.lazy(() => import('./pages/dispute/DisputeDetailsPage'));
const AssignDisputePage = React.lazy(() => import('./pages/dispute/AssignDisputePage'));

const PaymentPage = React.lazy(() => import('./pages/PaymentPage'));
const SubscriptionPage = React.lazy(() => import('./pages/SubscriptionPage'));
const ScanCreditPurchasePage = React.lazy(() => import('./pages/ScanCreditPurchasePage'));

// Import new collaboration and workflow pages
const InvestigatorListPage = React.lazy(() => import('./pages/investigator/InvestigatorListPage'));
const ActivityLogPage = React.lazy(() => import('./pages/investigator/ActivityLogPage'));

// Import search and audit pages
const EntitySearchPage = React.lazy(() => import('./pages/search/EntitySearchPage'));
const AuditLogPage = React.lazy(() => import('./pages/audit/AuditLogPage'));

const AnalyticsDashboardPage = React.lazy(() => import('./pages/analytics/AnalyticsDashboardPage'));

const DsrRequestPage = React.lazy(() => import('./pages/compliance/DsrRequestPage'));
const DsrStatusPage = React.lazy(() => import('./pages/compliance/DsrStatusPage'));

// Import settings pages
const WorkingHoursPage = React.lazy(() => import('./pages/settings/WorkingHoursPage'));

const EvidenceScoutDashboard = React.lazy(() => import('./pages/EvidenceScoutDashboard'));
const AnonymousEvidenceOrdersPage = React.lazy(() => import('./pages/AnonymousEvidenceOrdersPage'));
const CertificationPage = React.lazy(() => import('./pages/CertificationPage'));
const CertificationAuthorityDashboard = React.lazy(
  () => import('./components/CertificationAuthorityDashboard')
);
const SubmitEvidencePage = React.lazy(() => import('./pages/evidence-scout/SubmitEvidencePage'));
const OpportunitiesPage = React.lazy(() => import('./pages/evidence-scout/OpportunitiesPage'));
const MarketplacePage = React.lazy(() => import('./pages/evidence-scout/MarketplacePage'));
// Commented out: unused lazy imports for pages not yet routed
// const BidsPage = React.lazy(() => import('./pages/evidence-scout/BidsPage'));
// const HistoryPage = React.lazy(() => import('./pages/evidence-scout/HistoryPage'));
// const AnalyticsPage = React.lazy(() => import('./pages/evidence-scout/AnalyticsPage'));
// const EarningsDashboardPage = React.lazy(() => import('./pages/evidence-scout/EarningsDashboardPage'));
// const BadgeSystemPage = React.lazy(() => import('./pages/evidence-scout/BadgeSystemPage'));
// const LeaderboardSystemPage = React.lazy(() => import('./pages/evidence-scout/LeaderboardSystemPage'));
// const PerformanceAnalyticsPage = React.lazy(() => import('./pages/evidence-scout/PerformanceAnalyticsPage'));
// const WorkManagementDashboardPage = React.lazy(() => import('./pages/evidence-scout/WorkManagementDashboardPage'));
// const GigDetailsPage = React.lazy(() => import('./pages/evidence-scout/GigDetailsPage'));

// Import Universal Work Management
const UniversalWorkDashboardPage = React.lazy(() => import('./pages/work-management/UniversalWorkDashboardPage'));

// Import AI Agent Components
const AIAgentEvidenceScoutDashboard = React.lazy(() => import('./components/ai-agent/AIAgentEvidenceScoutDashboard'));
const AIAgentInvestigatorDashboard = React.lazy(() => import('./components/ai-agent/AIAgentInvestigatorDashboard'));
const AIAgentMarketplace = React.lazy(() => import('./components/ai-agent/AIAgentMarketplace'));
// const MachineReadableInterface = React.lazy(() => import('./components/ai-agent/MachineReadableInterface'));
const AIAgentLogin = React.lazy(() => import('./components/ai-agent/AIAgentLogin'));
const AIAgentDeveloperRegistration = React.lazy(() => import('./components/ai-agent/AIAgentDeveloperRegistration'));
const AIAgentEntryPoint = React.lazy(() => import('./pages/AIAgentEntryPoint'));
const VerifyScanConsentPage = React.lazy(() => import('./pages/VerifyScanConsentPage'));

// Expert Marketplace Pages
const ExpertMarketplacePage = React.lazy(() => import('./pages/expert/ExpertMarketplacePage'));
const ExpertEarningsPage = React.lazy(() => import('./pages/expert/ExpertEarningsPage'));
const ExpertPerformancePage = React.lazy(() => import('./pages/expert/ExpertPerformancePage'));
const ExpertPaymentSettingsPage = React.lazy(() => import('./pages/expert/ExpertPaymentSettingsPage'));

// HR Manager Pages
const HrExpertsPage = React.lazy(() => import('./pages/hr/HrExpertsPage'));
const HrExpertsCreatePage = React.lazy(() => import('./pages/hr/HrExpertsCreatePage'));
const HrEarningsPage = React.lazy(() => import('./pages/hr/HrEarningsPage'));

// Admin Pages
const AdminHrManagersPage = React.lazy(() => import('./pages/admin/AdminHrManagersPage'));
const AdminHrManagersCreatePage = React.lazy(() => import('./pages/admin/AdminHrManagersCreatePage'));
const AdminCommissionConfigPage = React.lazy(() => import('./pages/admin/AdminCommissionConfigPage'));
const AdminEarningsOverviewPage = React.lazy(() => import('./pages/admin/AdminEarningsOverviewPage'));
const AdminUsersPage = React.lazy(() => import('./pages/admin/AdminUsersPage'));
const AdminAdFunnelsPage = React.lazy(() => import('./pages/admin/AdminAdFunnelsPage'));
const AdminSecurityConfigPage = React.lazy(() => import('./pages/admin/security/AdminSecurityConfigPage'));
const AdminMediaProofConfigPage = React.lazy(() => import('./pages/admin/AdminMediaProofConfigPage'));
const AdminResearchPapersPage = React.lazy(() => import('./pages/admin/AdminResearchPapersPage'));
const AdminBlogCmsPage = React.lazy(() => import('./pages/admin/AdminBlogCmsPage'));
const AdminLeadsPage = React.lazy(() => import('./pages/admin/AdminLeadsPage'));
const AdminBlogAnalyticsPage = React.lazy(() => import('./pages/admin/AdminBlogAnalyticsPage'));
const AdminTeamCmsPage = React.lazy(() => import('./pages/admin/AdminTeamCmsPage'));
const AdminWorkInPublicPage = React.lazy(() => import('./pages/admin/AdminWorkInPublicPage'));
const AdminReferralSalesPage = React.lazy(() => import('./pages/admin/AdminReferralSalesPage'));
const AdminEngineGuidesPage = React.lazy(() => import('./pages/admin/AdminEngineGuidesPage'));
const AdminCaptionObservabilityPage = React.lazy(() => import('./pages/admin/AdminCaptionObservabilityPage'));
const AdminDistroObservabilityPage = React.lazy(() => import('./pages/admin/AdminDistroObservabilityPage'));

// Blog Pages (Public)
const BlogListPage = React.lazy(() => import('./pages/blog/BlogListPage'));
const BlogPostPage = React.lazy(() => import('./pages/blog/BlogPostPage'));
const TeamPage = React.lazy(() => import('./pages/TeamPage'));

// Ad Funnel Public Landing Page
const AdFunnelLandingPage = React.lazy(() => import('./pages/AdFunnelLandingPage'));
const BookLandingPage = React.lazy(() => import('./pages/BookLandingPage'));
const ShowroomProductLandingPage = React.lazy(() => import('./pages/ShowroomProductLandingPage'));
const AdFunnelServiceOfferPage = React.lazy(() => import('./pages/AdFunnelServiceOfferPage'));
const ReferralLandingPage = React.lazy(() => import('./pages/referral/ReferralLandingPage'));
const SalesOnboardingWizard = React.lazy(() => import('./pages/referral/SalesOnboardingWizard'));
const SalesAgentDashboard = React.lazy(() => import('./pages/referral/SalesAgentDashboard'));
const ApplyPage = React.lazy(() => import('./pages/referral/ApplyPage'));
// Import Product Picker Page
const ProductPickerPage = React.lazy(() => import('./pages/ProductPickerPage'));

const CaptionEngineLandingPage = React.lazy(() => import('./pages/caption/CaptionEngineLandingPage'));
const DistroEngineLandingPage = React.lazy(() => import('./pages/distro/DistroEngineLandingPage'));
const CaptionEngineConsole = React.lazy(() => import('./pages/caption/CaptionEngineConsole'));
const DistroEngineConsole = React.lazy(() => import('./pages/distro/DistroEngineConsole'));

// Demo Links Admin Pages (GDDE Wave 1)
const DemoLinksPage = React.lazy(() => import('./pages/admin/demo-links/DemoLinksPage'));
const CreateDemoLinkPage = React.lazy(() => import('./pages/admin/demo-links/CreateDemoLinkPage'));
const DemoLinkAnalyticsPage = React.lazy(() => import('./pages/admin/demo-links/DemoLinkAnalyticsPage'));

// Demo Engine Payment Pages (GDDE Wave 1 - Public)
const PaymentSuccessPage = React.lazy(() => import('./demo-engine/pages/PaymentSuccessPage'));
const PaymentCancelPage = React.lazy(() => import('./demo-engine/pages/PaymentCancelPage'));

// Demo Engine Core Components (GDDE - Industry Demo Dashboard)
const DemoEngineProvider = React.lazy(() =>
  import('./demo-engine').then((mod) => ({ default: mod.DemoEngineProvider }))
);
const DynamicThemeProvider = React.lazy(() =>
  import('./demo-engine').then((mod) => ({ default: mod.DynamicThemeProvider }))
);
const DemoLayout = React.lazy(() =>
  import('./demo-engine').then((mod) => ({ default: mod.DemoLayout }))
);
const DemoLayoutWrapper = React.lazy(() =>
  import('./demo-engine').then((mod) => ({ default: mod.DemoLayoutWrapper }))
);
const DashboardResolver = React.lazy(() =>
  import('./demo-engine').then((mod) => ({ default: mod.DashboardResolver }))
);
const OnboardingWizard = React.lazy(() =>
  import('./demo-engine').then((mod) => ({ default: mod.OnboardingWizard }))
);

// Guardian Infra Demo Page (Phase 3)
const InfraDemoPage = React.lazy(() => import('./pages/InfraDemoPage'));
const CliDocsPage = React.lazy(() => import('./pages/CliDocsPage'));
const DistroCliDocsPage = React.lazy(() => import('./pages/DistroCliDocsPage'));
const ServicesPage = React.lazy(() => import('./pages/ServicesPage'));

// Auth Pages
const MagicLinkPage = React.lazy(() => import('./pages/auth/MagicLinkPage'));
const NavigationFabs = React.lazy(() =>
  import('./components/common/NavigationFabs').then((mod) => ({ default: mod.NavigationFabs }))
);

const SecurityRuntime: React.FC = () => {
  const location = useLocation();

  useSecurityMeasures({
    preventDevTools: securityConfig.features.preventDevTools,
    preventCopy: securityConfig.features.preventCopy,
    preventRightClick: securityConfig.features.preventRightClick,
    preventKeyboardShortcuts: securityConfig.features.preventKeyboardShortcuts,
    disableConsole: securityConfig.features.disableConsole,
    currentPath: location.pathname,
  });

  return null;
};

// Normalize non-breaking hyphens (U+2011) from printed book/eBook URLs to standard hyphens (U+002D)
// Handles both literal non-breaking hyphens and URL-encoded versions (%E2%80%91) case-insensitively.
if (typeof window !== 'undefined') {
  const rawPath = window.location.pathname;
  const rawSearch = window.location.search;
  const hasU2011 = rawPath.includes('\u2011') || rawSearch.includes('\u2011');
  const hasEncoded = /%E2%80%91/i.test(rawPath) || /%E2%80%91/i.test(rawSearch);

  if (hasU2011 || hasEncoded) {
    let cleanPath = rawPath;
    let cleanSearch = rawSearch;

    if (hasEncoded) {
      try {
        cleanPath = decodeURIComponent(rawPath).replace(/\u2011/g, '-');
        cleanSearch = decodeURIComponent(rawSearch).replace(/\u2011/g, '-');
      } catch {
        cleanPath = rawPath.replace(/%E2%80%91/gi, '-');
        cleanSearch = rawSearch.replace(/%E2%80%91/gi, '-');
      }
    } else {
      cleanPath = rawPath.replace(/\u2011/g, '-');
      cleanSearch = rawSearch.replace(/\u2011/g, '-');
    }

    window.history.replaceState(null, '', cleanPath + cleanSearch + window.location.hash);
  }
}

const App: React.FC = () => {
  React.useEffect(() => {
    captureReferralParam();
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <BrowserRouter>
            <ScrollToTop />
            <SecurityRuntime />
            <NotificationProvider>
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <ResponsiveAppBar />
                <Box component="main" sx={{ flexGrow: 1, pt: { xs: '56px', sm: '64px' }, display: 'flex', flexDirection: 'column', width: '100%' }}>
                  <Suspense fallback={
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                      <CircularProgress />
                    </Box>
                  }>
                    <Routes>
                      {/* Public routes */}
                      <Route path="/" element={<LandingPage />} />
                      <Route path="/work-in-public" element={<LandingPage />} />
                      <Route path="/whitepaper" element={<Whitepaper />} />
                      {/* Pre-login product picker — acts as the new /login landing */}
                      <Route path="/login" element={<PreLoginProductPicker />} />
                      {/* Product-specific login pages */}
                      <Route path="/login/product" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/reset-password/:token" element={<ResetPassword />} />
                      <Route path="/verify-email/:token" element={<VerifyEmail />} />
                      <Route path="/verify-2fa" element={
                        <TwoFactorVerification
                          onVerify={async () => { }}
                          onUseBackupCode={() => { }}
                          email="dev@localhost"
                        />
                      } />
                      <Route path="/verify-scan-consent" element={<VerifyScanConsentPage />} />
                      <Route path="/sandbox" element={<SandboxLandingPage />} />
                      <Route path="/ad-funnel/:id" element={<AdFunnelLandingPage />} />
                      <Route path="/books/:slug" element={<BookLandingPage />} />
                      <Route path="/showroom/:slug" element={<ShowroomProductLandingPage />} />
                      <Route path="/services/:slug" element={<AdFunnelServiceOfferPage />} />
                      <Route path="/referral-program" element={<ReferralLandingPage />} />
                      <Route path="/referral/apply" element={<ApplyPage />} />
                      <Route path="/sales/onboarding" element={
                        <ProtectedRoute>
                          <SalesOnboardingWizard />
                        </ProtectedRoute>
                      } />
                      <Route path="/sales/dashboard" element={
                        <ProtectedRoute>
                          <SalesAgentDashboard />
                        </ProtectedRoute>
                      } />
                      <Route path="/caption-engine" element={<CaptionEngineLandingPage />} />
                      <Route path="/distro-engine" element={<DistroEngineLandingPage />} />
                      <Route path="/family-care" element={<FamilyCareLandingPage />} />
                      <Route path="/family-care/help" element={<FamilyCareGuidePage />} />
                      <Route path="/family-care/caregivers" element={<FamilyCareCaregiverApplicationPage />} />
                      <Route path="/family-care/caregivers/invite" element={<FamilyCareCaregiverInvitePage />} />
                      <Route path="/family-care/book" element={<FamilyCareBookingPage />} />
                      <Route path="/family-care/booking/complete" element={<FamilyCareBookingCompletePage />} />
                      <Route path="/family-care/bookings" element={
                        <ProtectedRoute>
                          <FamilyCareParentDashboard />
                        </ProtectedRoute>
                      } />
                      <Route path="/family-care/worker/help" element={
                        <ProtectedRoute requiredRole={["CAREGIVER", "Caregiver"]}>
                          <FamilyCareGuidePage />
                        </ProtectedRoute>
                      } />
                      <Route path="/family-care/worker" element={
                        <ProtectedRoute requiredRole={["CAREGIVER", "Caregiver"]}>
                          <FamilyCareWorkerDashboard />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/product-picker" element={
                        <ProtectedRoute requiredRole={["BUSINESS_CONTENT_CREATOR", "BusinessContentCreator"]}>
                          <ProductPickerPage />
                        </ProtectedRoute>
                      } />
                      
                      <Route path="/console/caption-engine" element={
                        <ProtectedRoute requiredRole={["BUSINESS_CONTENT_CREATOR", "BusinessContentCreator", "ADMIN"]}>
                          <CaptionEngineConsole />
                        </ProtectedRoute>
                      } />
                      <Route path="/console/distro-engine" element={
                        <ProtectedRoute requiredRole={["ADMIN", "BUSINESS_CONTENT_CREATOR", "BusinessContentCreator"]}>
                          <DistroEngineConsole />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/caption-engine" element={
                        <ProtectedRoute requiredRole={["ADMIN"]}>
                          <AdminCaptionObservabilityPage />
                        </ProtectedRoute>
                      } />
                      <Route path="/admin/distro-engine" element={
                        <ProtectedRoute requiredRole={["ADMIN"]}>
                          <AdminDistroObservabilityPage />
                        </ProtectedRoute>
                      } />

                      {/* Legal Pages */}
                      <Route path="/terms" element={<TermsOfService />} />
                      <Route path="/privacy" element={<PrivacyPolicy />} />
                      <Route path="/contact" element={<ContactUs />} />

                      {/* OAuth Callback Routes */}
                      <Route path="/auth/callback/github" element={<OAuthCallback />} />
                      <Route path="/auth/callback/google" element={<OAuthCallback />} />

                      {/* Onboarding Routes */}
                      <Route path="/onboarding/select-role" element={<SelectRole />} />

                      {/* --- Enterprise Landing Page Routes (Public) --- */}
                      <Route path="/enterprise/vcn" element={<VCNLandingPage />} />
                      <Route path="/enterprise/gateway-sniffer" element={<GatewaySnifferLandingPage />} />
                      <Route path="/enterprise/gatekeeperx" element={<GateKeeperXLandingPage />} />

                      {/* Guardian Infra Demo (Public - Phase 3) */}
                      <Route path="/infra-demo" element={<InfraDemoPage />} />
                      <Route path="/cli-docs" element={<CliDocsPage />} />
                      <Route path="/distroengine-cli-docs" element={<DistroCliDocsPage />} />

                      {/* Services & Pricing (Public) */}
                      <Route path="/services" element={<ServicesPage />} />
                      
                      <Route path="/media-proof" element={<MediaProofLandingPage />} />
                      <Route path="/rag-integrity-sentinel" element={<RagIntegritySentinelLandingPage />} />
                      <Route path="/governable-ai" element={<GovernableAiLandingPage />} />
                      <Route path="/governable-ai/toolkit" element={<GovernableAiToolkitPage />} />
                      <Route path="/governable-ai/toolkit/:toolSlug" element={<GovernableAiToolkitPage />} />
                      <Route path="/governable-ai/workspace" element={<GovernableAiWorkspacePage />} />

                      {/* Research Papers Page (Public) */}
                      <Route path="/research-papers" element={<ResearchPapersPage />} />
                      <Route path="/team" element={<TeamPage />} />

                      {/* Native Tongue Initiative Page (Public) */}
                      <Route path="/native-tongue" element={<NativeTongueLandingPage />} />

                      {/* Blog & Research Routes (Public) */}
                      <Route path="/blog" element={<BlogListPage />} />
                      <Route path="/blog/:slug" element={<BlogPostPage />} />

                      {/* Protected routes */}
                      <Route
                        path="/dashboard"
                        element={
                          <ProtectedRoute>
                            <Dashboard />
                          </ProtectedRoute>
                        }
                      />
                      {/* Fraud Reporting Routes */}
                      <Route
                        path="/fraud/submit"
                        element={
                          <ProtectedRoute>
                            <SubmitFraudReportPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/fraud/my-reports"
                        element={
                          <ProtectedRoute>
                            <MyReportsPage />
                          </ProtectedRoute>
                        }
                      />
                      {/* Fraud Report Detail Route */}
                      <Route
                        path="/fraud/report/:reportId"
                        element={
                          <ProtectedRoute>
                            <ReportDetailsPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Dispute Routes */}
                      <Route
                        path="/dispute/submit"
                        element={
                          <ProtectedRoute>
                            <SubmitDisputePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dispute/my-disputes"
                        element={
                          <ProtectedRoute>
                            <MyDisputesPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dispute/details/:disputeId"
                        element={
                          <ProtectedRoute>
                            <DisputeDetailsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dispute/assign/:disputeId"
                        element={
                          <ProtectedRoute>
                            <AssignDisputePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dispute/activity-log"
                        element={
                          <ProtectedRoute>
                            <ActivityLogPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Universal Work Management Routes */}
                      <Route
                        path="/work-management"
                        element={
                          <ProtectedRoute>
                            <UniversalWorkDashboardPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Investigator Routes */}
                      <Route
                        path="/fraud/investigator-assigned"
                        element={
                          <ProtectedRoute>
                            <InvestigatorAssignedReportsPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Investigator Marketplace Routes */}
                      <Route
                        path="/investigator/marketplace"
                        element={
                          <ProtectedRoute requiredRole={["INVESTIGATOR", "ADMIN"]}>
                            <InvestigatorMarketplacePage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/trust-score/scan"
                        element={
                          <ProtectedRoute>
                            <TrustScoreScanPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/trust-score/dashboard"
                        element={
                          <ProtectedRoute>
                            <TrustScoreDashboardPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/trust-score/details/:scanId"
                        element={
                          <ProtectedRoute>
                            <TrustScoreDetailsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/trust-score/analytics"
                        element={
                          <ProtectedRoute>
                            <TrustScoreAnalyticsPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Anonymous Evidence Orders Route */}
                      <Route
                        path="/evidence/orders"
                        element={
                          <ProtectedRoute>
                            <AnonymousEvidenceOrdersPage />
                          </ProtectedRoute>
                        }
                      />

                      <Route
                        path="/admin/dashboard"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN"]}>
                            <AdminDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/sales"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN"]}>
                            <AdminReferralSalesPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/evidence-scout/dashboard"
                        element={
                          <ProtectedRoute requiredRole={["EVIDENCE_SCOUT", "ADMIN", "INVESTIGATOR"]}>
                            <EvidenceScoutDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/evidence-scout/submit/:opportunityId"
                        element={
                          <ProtectedRoute requiredRole={["EVIDENCE_SCOUT", "ADMIN", "INVESTIGATOR"]}>
                            <SubmitEvidencePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/evidence-scout/opportunities"
                        element={
                          <ProtectedRoute requiredRole={["EVIDENCE_SCOUT", "ADMIN", "INVESTIGATOR"]}>
                            <OpportunitiesPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/evidence-scout/marketplace"
                        element={
                          <ProtectedRoute requiredRole={["EVIDENCE_SCOUT", "ADMIN", "INVESTIGATOR"]}>
                            <MarketplacePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/certification"
                        element={
                          <ProtectedRoute requiredRole={["EVIDENCE_SCOUT", "INVESTIGATOR", "ADMIN"]}>
                            <CertificationPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Certification Authority Routes */}
                      <Route
                        path="/certification-authority/dashboard"
                        element={
                          <ProtectedRoute requiredRole={["CERTIFICATION_AUTHORITY", "ADMIN"]}>
                            <CertificationAuthorityDashboard />
                          </ProtectedRoute>
                        }
                      />

                      {/* AI Agent Routes */}
                      <Route
                        path="/ai-agent/evidence-scout/dashboard"
                        element={
                          <ProtectedRoute requiredRole={["AI_AGENT_EVIDENCE_SCOUT", "ADMIN"]}>
                            <AIAgentEvidenceScoutDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/ai-agent/investigator/dashboard"
                        element={
                          <ProtectedRoute requiredRole={["AI_AGENT_INVESTIGATOR", "ADMIN"]}>
                            <AIAgentInvestigatorDashboard />
                          </ProtectedRoute>
                        }
                      />

                      {/* Machine-Readable AI Agent Interfaces */}
                      <Route
                        path="/ai-agent/evidence-scout/machine-interface"
                        element={
                          <ProtectedRoute requiredRole={["AI_AGENT_EVIDENCE_SCOUT", "ADMIN"]}>
                            <AIAgentEvidenceScoutDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/ai-agent/investigator/machine-interface"
                        element={
                          <ProtectedRoute requiredRole={["AI_AGENT_INVESTIGATOR", "ADMIN"]}>
                            <AIAgentInvestigatorDashboard />
                          </ProtectedRoute>
                        }
                      />

                      {/* AI Agent Evidence Scout Specific Routes */}
                      <Route
                        path="/ai-agent/evidence-scout/marketplace"
                        element={
                          <ProtectedRoute requiredRole={["AI_AGENT_EVIDENCE_SCOUT", "ADMIN"]}>
                            <AIAgentEvidenceScoutDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/ai-agent/evidence-scout/opportunities"
                        element={
                          <ProtectedRoute requiredRole={["AI_AGENT_EVIDENCE_SCOUT", "ADMIN"]}>
                            <AIAgentEvidenceScoutDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/ai-agent/evidence-scout/submit"
                        element={
                          <ProtectedRoute requiredRole={["AI_AGENT_EVIDENCE_SCOUT", "ADMIN"]}>
                            <AIAgentEvidenceScoutDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/ai-agent/evidence-scout/analytics"
                        element={
                          <ProtectedRoute requiredRole={["AI_AGENT_EVIDENCE_SCOUT", "ADMIN"]}>
                            <AIAgentEvidenceScoutDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/ai-agent/evidence-scout/bids"
                        element={
                          <ProtectedRoute requiredRole={["AI_AGENT_EVIDENCE_SCOUT", "ADMIN"]}>
                            <AIAgentEvidenceScoutDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/ai-agent/evidence-scout/earnings"
                        element={
                          <ProtectedRoute requiredRole={["AI_AGENT_EVIDENCE_SCOUT", "ADMIN"]}>
                            <AIAgentEvidenceScoutDashboard />
                          </ProtectedRoute>
                        }
                      />

                      {/* AI Agent Investigator Specific Routes */}
                      <Route
                        path="/ai-agent/investigator/marketplace"
                        element={
                          <ProtectedRoute requiredRole={["AI_AGENT_INVESTIGATOR", "ADMIN"]}>
                            <AIAgentInvestigatorDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/ai-agent/investigator/cases"
                        element={
                          <ProtectedRoute requiredRole={["AI_AGENT_INVESTIGATOR", "ADMIN"]}>
                            <AIAgentInvestigatorDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/ai-agent/investigator/evidence"
                        element={
                          <ProtectedRoute requiredRole={["AI_AGENT_INVESTIGATOR", "ADMIN"]}>
                            <AIAgentInvestigatorDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/ai-agent/investigator/analytics"
                        element={
                          <ProtectedRoute requiredRole={["AI_AGENT_INVESTIGATOR", "ADMIN"]}>
                            <AIAgentInvestigatorDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/ai-agent/investigator/bids"
                        element={
                          <ProtectedRoute requiredRole={["AI_AGENT_INVESTIGATOR", "ADMIN"]}>
                            <AIAgentInvestigatorDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/ai-agent/investigator/earnings"
                        element={
                          <ProtectedRoute requiredRole={["AI_AGENT_INVESTIGATOR", "ADMIN"]}>
                            <AIAgentInvestigatorDashboard />
                          </ProtectedRoute>
                        }
                      />

                      {/* AI Agent Marketplace */}
                      <Route
                        path="/ai-agent/marketplace"
                        element={
                          <ProtectedRoute requiredRole={["AI_AGENT_EVIDENCE_SCOUT", "AI_AGENT_INVESTIGATOR", "ADMIN"]}>
                            <AIAgentMarketplace />
                          </ProtectedRoute>
                        }
                      />

                      {/* AI Agent Entry Point */}
                      <Route
                        path="/ai-agent"
                        element={<AIAgentEntryPoint />}
                      />

                      {/* AI Agent Developer Registration */}
                      <Route
                        path="/ai-agent/developer/register"
                        element={<AIAgentDeveloperRegistration />}
                      />

                      {/* AI Agent Login Interfaces */}
                      <Route
                        path="/ai-agent/login/evidence-scout"
                        element={<AIAgentLogin agentType="AI_AGENT_EVIDENCE_SCOUT" />}
                      />
                      <Route
                        path="/ai-agent/login/investigator"
                        element={<AIAgentLogin agentType="AI_AGENT_INVESTIGATOR" />}
                      />

                      {/* Expert Marketplace Routes */}
                      <Route
                        path="/expert/marketplace"
                        element={
                          <ProtectedRoute requiredRole={["CYBERSECURITY_EXPERT", "ADMIN"]}>
                            <ExpertMarketplacePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/expert/earnings"
                        element={
                          <ProtectedRoute requiredRole={["CYBERSECURITY_EXPERT", "ADMIN"]}>
                            <ExpertEarningsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/expert/performance"
                        element={
                          <ProtectedRoute requiredRole={["CYBERSECURITY_EXPERT", "ADMIN"]}>
                            <ExpertPerformancePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/expert/settings/payment"
                        element={
                          <ProtectedRoute requiredRole={["CYBERSECURITY_EXPERT", "ADMIN"]}>
                            <ExpertPaymentSettingsPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* HR Manager Routes */}
                      <Route
                        path="/hr/experts"
                        element={
                          <ProtectedRoute requiredRole={["HR_MANAGER", "ADMIN"]}>
                            <HrExpertsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/hr/experts/create"
                        element={
                          <ProtectedRoute requiredRole={["HR_MANAGER", "ADMIN"]}>
                            <HrExpertsCreatePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/hr/earnings"
                        element={
                          <ProtectedRoute requiredRole={["HR_MANAGER", "ADMIN"]}>
                            <HrEarningsPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Admin HR Manager Routes */}
                      <Route
                        path="/admin/family-care"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN", "Admin", "SUPER_ADMIN", "SUPERADMIN", "SuperAdmin"]}>
                            <FamilyCareAdminDashboard />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/family-care/help"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN", "Admin", "SUPER_ADMIN", "SUPERADMIN", "SuperAdmin"]}>
                            <FamilyCareAdminGuidePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/family-care/settings"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN", "Admin", "SUPER_ADMIN", "SUPERADMIN", "SuperAdmin"]}>
                            <FamilyCareSettingsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/hr-managers"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN"]}>
                            <AdminHrManagersPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/hr-managers/create"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN"]}>
                            <AdminHrManagersCreatePage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Demo Links Admin Routes (GDDE Wave 1) */}
                      <Route
                        path="/admin/ad-funnels"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN"]}>
                            <AdminAdFunnelsPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/security-config"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN"]}>
                            <AdminSecurityConfigPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/media-proof-config"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN"]}>
                            <AdminMediaProofConfigPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/research-papers"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN"]}>
                            <AdminResearchPapersPage />
                          </ProtectedRoute>
                        }
                      />
                       <Route
                         path="/admin/work-in-public"
                         element={
                           <ProtectedRoute requiredRole={["ADMIN"]}>
                             <AdminWorkInPublicPage />
                           </ProtectedRoute>
                         }
                       />
                       <Route
                         path="/admin/engine-guides"
                         element={
                           <ProtectedRoute requiredRole={["ADMIN"]}>
                             <AdminEngineGuidesPage />
                           </ProtectedRoute>
                         }
                       />
                       <Route
                         path="/admin/blog"
                         element={
                           <ProtectedRoute requiredRole={["ADMIN"]}>
                             <AdminBlogCmsPage />
                           </ProtectedRoute>
                         }
                       />
                       <Route
                         path="/admin/team"
                         element={
                           <ProtectedRoute requiredRole={["ADMIN"]}>
                             <AdminTeamCmsPage />
                           </ProtectedRoute>
                         }
                       />
                       <Route
                         path="/admin/leads"
                         element={
                           <ProtectedRoute requiredRole={["ADMIN"]}>
                             <AdminLeadsPage />
                           </ProtectedRoute>
                         }
                       />
                       <Route
                         path="/admin/blog-analytics"
                         element={
                           <ProtectedRoute requiredRole={["ADMIN"]}>
                             <AdminBlogAnalyticsPage />
                           </ProtectedRoute>
                         }
                       />
                      <Route
                        path="/admin/demo-links"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN"]}>
                            <DemoLinksPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/demo-links/create"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN"]}>
                            <CreateDemoLinkPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/demo-links/:slug/analytics"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN"]}>
                            <DemoLinkAnalyticsPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Commission & Earnings Admin Routes */}
                      <Route
                        path="/admin/commission-config"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN"]}>
                            <AdminCommissionConfigPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/earnings-overview"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN"]}>
                            <AdminEarningsOverviewPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Magic Link Auth Route (Public) */}
                      <Route
                        path="/auth/magic-link"
                        element={<MagicLinkPage />}
                      />

                      {/* Demo Engine Routes (GDDE - Public Demo Dashboard) */}
                      <Route
                        path="/demo"
                        element={
                          <DemoEngineProvider>
                            <DynamicThemeProvider>
                              <DemoLayoutWrapper>
                                <DemoLayout />
                              </DemoLayoutWrapper>
                            </DynamicThemeProvider>
                          </DemoEngineProvider>
                        }
                      >
                        {/* Index route renders the dashboard */}
                        <Route index element={<DashboardResolver />} />
                        <Route path="dashboard" element={<DashboardResolver />} />
                        <Route path="start" element={<OnboardingWizard />} />
                      </Route>

                      {/* Demo Engine Payment Routes (Public - Stripe Checkout) */}
                      <Route
                        path="/demo/payment/success"
                        element={<PaymentSuccessPage />}
                      />
                      <Route
                        path="/demo/payment/cancel"
                        element={<PaymentCancelPage />}
                      />

                      <Route
                        path="/dashboard/payment"
                        element={<Navigate to="/payment" replace />}
                      />
                      <Route
                        path="/payment"
                        element={
                          <ProtectedRoute>
                            <PaymentPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/subscription"
                        element={
                          <ProtectedRoute>
                            <SubscriptionPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/scan-credits"
                        element={
                          <ProtectedRoute>
                            <ScanCreditPurchasePage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/fraud-reporting"
                        element={
                          <ProtectedRoute>
                            <FraudReporting />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/support"
                        element={
                          <ProtectedRoute>
                            <Support />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/sessions"
                        element={
                          <ProtectedRoute>
                            <SessionManagement />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/social-accounts"
                        element={
                          <ProtectedRoute>
                            <SocialAccountManager />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/2fa-setup"
                        element={
                          <ProtectedRoute>
                            <TwoFactorSetup onComplete={() => { }} />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/dashboard/profile"
                        element={
                          <ProtectedRoute>
                            <Profile />
                          </ProtectedRoute>
                        }
                      />

                      {/* Collaboration & Workflow Routes */}
                      <Route
                        path="/investigator/list"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN", "INVESTIGATOR"]}>
                            <InvestigatorListPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/investigator/activity-log"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN", "INVESTIGATOR"]}>
                            <ActivityLogPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Search & Audit Routes */}
                      <Route
                        path="/search/entity"
                        element={
                          <ProtectedRoute>
                            <EntitySearchPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/audit/log"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN", "INVESTIGATOR"]}>
                            <AuditLogPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Analytics & Reporting Routes */}
                      <Route
                        path="/analytics/dashboard"
                        element={
                          <ProtectedRoute>
                            <AnalyticsDashboardPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Compliance & DSR Routes */}
                      <Route
                        path="/compliance/dsr-request"
                        element={
                          <ProtectedRoute>
                            <DsrRequestPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/compliance/dsr-status"
                        element={
                          <ProtectedRoute>
                            <DsrStatusPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Settings Routes */}
                      <Route
                        path="/settings/working-hours"
                        element={
                          <ProtectedRoute>
                            <WorkingHoursPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* --- Enterprise Feature Routes (Protected) --- */}
                      <Route
                        path="/enterprise/vcn/setup"
                        element={
                          <ProtectedRoute>
                            <VCNPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/enterprise/gateway-sniffer/feature"
                        element={
                          <ProtectedRoute>
                            <GatewaySnifferPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/enterprise/gatekeeperx/feature"
                        element={
                          <ProtectedRoute>
                            <GateKeeperXPage />
                          </ProtectedRoute>
                        }
                      />

                      {/* Admin routes */}
                      <Route
                        path="/admin/users"
                        element={
                          <ProtectedRoute requiredRole={["ADMIN"]}>
                            <AdminUsersPage />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/*"
                        element={
                          <ProtectedRoute requiredRole="admin">
                            <div>Admin area (add admin components here)</div>
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/fraud/investigator-assigned"
                        element={
                          <ProtectedRoute requiredRole={["INVESTIGATOR", "ADMIN"]}>
                            <InvestigatorAssignedReportsPage />
                          </ProtectedRoute>
                        }
                      />
                      {/* Catch-all route: redirect unknown paths to LandingPage */}
                      <Route path="*" element={<LandingPage />} />
                    </Routes>
                  </Suspense>
                </Box>

                {/* Navigation FABs for back navigation and dashboard access */}
                <Suspense fallback={null}>
                  <NavigationFabs />
                </Suspense>
              </Box>
            </NotificationProvider>
          </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

export default App;
